import { readClassification } from "@/lib/esco";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import {
  DATE_FIELDS,
  entryDateError,
  entryDateMessage,
  entryFieldError,
  entryFieldMessage,
} from "./profile/entry-validation";
import { recipientRelations, type ContentRecipient } from "@/lib/content-sharing";
import { coupleLanguagePatch } from "@/lib/language-options";
import type { UseCertificatesResult, CertificateFormInput } from "./use-certificates";
import type { UseEducationResult, EducationFormInput } from "./use-education";
import type { UseExperiencesResult, ExperienceFormInput } from "./use-experiences";
import type { UseExpertiseSkillsResult, SkillFormInput } from "./use-expertise-skills";
import type { UseLanguagesResult, LanguageFormInput } from "./use-languages";
import { byStartNewestFirst } from "@/lib/entry-order";
import { userFacingGraphqlError } from "@/lib/graphql-error";
import { LocalizedError } from "@/lib/localized-error";

// Holds the SaveBar-deferred state for every Entry aggregate on the Save ·
// Profile page (experiences / educations / expertises / certificates /
// languages). Replaces the old per-row debounced auto-save model (see
// @adr SPARK-0005-unified-deferred-save-bar@1): all entry mutations now ride the page-level SaveBar.
//
// Three overlays per kind:
//   edits[kind][id]   - partial patch over an existing genesis row, keyed
//                       by real id. Wiped on successful commit.
//   drafts[kind]      - local-only rows with synthetic ids ("draft-…"),
//                       never seen by genesis until SaveBar fires.
//   errors[kind:id]   - per-row failure messages from the last commit.
//                       Cleared as soon as the row's input changes again.
//
// `save()` runs mutations sequentially: entries share person search-index and
// access-segment locks in Genesis. Each settled outcome updates state independently:
// successes clear their overlay slice, failures keep theirs + write an
// error. A trailing throw with the failure count surfaces to the SaveBar's
// catch block.
//
// What a save created or removed reaches the server rows only with a reload, and not always the first
// one: Apollo shares a request already in flight, and it may have left before the last write. So
// `landed` stands in for a created row, under its server id, until the server rows hold it, and
// `removed` hides a removed one for good. Without them the section went without the saved entry in
// between, and an add form opened in that gap was torn down when the entry came back (spark#315).

export type EntryKind = "experience" | "education" | "expertise" | "certificate" | "language";

export const ENTRY_KINDS: readonly EntryKind[] = [
  "experience",
  "education",
  "expertise",
  "certificate",
  "language",
] as const;

const DRAFT_PREFIX = "draft-";

export const isDraftId = (id: string): boolean => id.startsWith(DRAFT_PREFIX);

// Entry rows flow through this orchestrator in a loose, kind-agnostic shape:
// genesis rows get normalized to UI primitives, overlays spread partial
// patches on top, and the result is fed to a per-kind typed builder before
// it reaches a mutation. `unknown` (not `any`) keeps the seam honest - every
// field read goes through a narrowing helper (str / refId / Boolean / …).
type EntryValues = Record<string, unknown>;

interface DraftRow {
  id: string;
  values: EntryValues;
}

type EntryEdits = Record<EntryKind, Record<string, EntryValues>>;
type EntryDrafts = Record<EntryKind, DraftRow[]>;
type EntryDeletes = Record<EntryKind, ReadonlySet<string>>;
type EntryErrors = Record<string, string>; // key = `${kind}:${id}`

// A foreign language (not a native speaker) must carry a CEFR level. The
// add/edit panels gate on it, but edits stage straight into the SaveBar, so the
// commit guard below enforces it once more. The message is localized via
// react-intl (@adr SPARK-0013-i18n-mandatory-via-react-intl@1) and surfaced verbatim per row by the SaveBar.

const EMPTY_EDITS: EntryEdits = {
  experience: {},
  education: {},
  expertise: {},
  certificate: {},
  language: {},
};

const EMPTY_DRAFTS: EntryDrafts = {
  experience: [],
  education: [],
  expertise: [],
  certificate: [],
  language: [],
};

const EMPTY_DELETES: EntryDeletes = {
  experience: new Set(),
  education: new Set(),
  expertise: new Set(),
  certificate: new Set(),
  language: new Set(),
};

export interface UseProfileEntriesStateArgs {
  myself: {
    experiences?: readonly EntryValues[] | null;
    educations?: readonly EntryValues[] | null;
    expertises?: readonly EntryValues[] | null;
    certifications?: readonly EntryValues[] | null;
    languageSkills?: readonly EntryValues[] | null;
  } | null;
  hooks: {
    experience: UseExperiencesResult;
    education: UseEducationResult;
    expertise: UseExpertiseSkillsResult;
    certificate: UseCertificatesResult;
    language: UseLanguagesResult;
  };
  /**
   * Reads the date of birth a save WOULD commit (pending edit included), so an entry date is checked
   * against the value being saved rather than the stale stored one — matching how the import review
   * resolves it via committedDateOfBirth. A function because the profile's merged value is derived
   * further down ProfilePage than this hook is called, and because it must be read at save time.
   * Returning blank simply skips the before-birth rule.
   */
  getBirthDate?: () => string | null | undefined;
  defaultRecipients?: readonly ContentRecipient[];
}

export interface MergedEntries {
  experience: EntryValues[];
  education: EntryValues[];
  expertise: EntryValues[];
  certificate: EntryValues[];
  language: EntryValues[];
}

export interface UseProfileEntriesStateResult {
  /** Merged display arrays - saved rows from the server + local drafts at the end. */
  entries: MergedEntries;
  /** Synthetic ids of currently-live drafts, for callers that need to render them differently. */
  draftIds: ReadonlySet<string>;
  /** Saved-row ids staged for deletion on the next save. Drafts are not eligible - ABBRECHEN removes them directly. */
  pendingDeletes: Readonly<EntryDeletes>;
  /** Per-row error messages. Keys are `${kind}:${id}`. */
  errors: Readonly<EntryErrors>;
  /** Apply a partial update to a row's overlay. Branches on real-id vs draft-id internally. */
  patchEntry: (kind: EntryKind, id: string, patch: EntryValues) => void;
  /**
   * Drop a row's overlay. For drafts, removes the draft entirely; for
   * existing rows, reverts the edit slice AND un-marks any pending
   * deletion. Idempotent if nothing was staged.
   */
  cancelEntry: (kind: EntryKind, id: string) => void;
  /** Create a new draft row from an AddForm payload. */
  addDraft: (kind: EntryKind, payload: EntryValues) => string;
  /**
   * Stage an existing saved row for deletion on the next save. No-op for
   * drafts (caller should `cancelEntry` instead). Editing a delete-staged
   * row implicitly un-stages - but the explicit path is `cancelEntry`.
   */
  markForDelete: (kind: EntryKind, id: string) => void;
  /** True iff anything in this slice would produce a mutation on save. */
  dirty: boolean;
  /**
   * Fire all pending mutations one after another. Successes clear their overlay slice; failures keep
   * theirs and write an error. Created rows stay on screen and removed ones hidden until the server
   * rows agree. Throws an aggregate Error if any mutation failed.
   */
  save: () => Promise<void>;
  /** Drop every overlay (edits, drafts, errors) without firing any mutation. What a save wrote stays. */
  discard: () => void;
}

export function useProfileEntriesState({
  myself,
  hooks,
  getBirthDate,
  defaultRecipients = [],
}: UseProfileEntriesStateArgs): UseProfileEntriesStateResult {
  const intl = useIntl();
  const [edits, setEdits] = useState<EntryEdits>(EMPTY_EDITS);
  const [drafts, setDrafts] = useState<EntryDrafts>(EMPTY_DRAFTS);
  const [deletes, setDeletes] = useState<EntryDeletes>(EMPTY_DELETES);
  const [errors, setErrors] = useState<EntryErrors>({});
  const [landed, setLanded] = useState<EntryDrafts>(EMPTY_DRAFTS);
  const [removed, setRemoved] = useState<EntryDeletes>(EMPTY_DELETES);

  // The overlays as last rendered, read while the mutations run: typing or a cancel during them shows.
  const overlaysNow = useRef({ drafts, edits, deletes });
  useEffect(() => {
    overlaysNow.current = { drafts, edits, deletes };
  }, [drafts, edits, deletes]);

  const draftIds = useMemo<ReadonlySet<string>>(() => {
    const ids = new Set<string>();
    for (const kind of ENTRY_KINDS) for (const d of drafts[kind]) ids.add(d.id);
    return ids;
  }, [drafts]);

  const patchEntry = useCallback(
    (kind: EntryKind, id: string, patch: EntryValues) => {
      if (!id) return;
      // Clear any stale error for this row - the user is editing, the
      // previous failure no longer reflects what's about to be sent.
      setErrors((prev) => {
        const key = `${kind}:${id}`;
        if (!(key in prev)) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
      const effective = kind === "language" ? coupleLanguagePatch(patch) : patch;
      if (isDraftId(id)) {
        setDrafts((prev) => ({
          ...prev,
          [kind]: prev[kind].map((d) =>
            d.id === id ? { ...d, values: { ...d.values, ...effective } } : d,
          ),
        }));
      } else {
        setEdits((prev) => ({
          ...prev,
          [kind]: {
            ...prev[kind],
            [id]: { ...(prev[kind][id] ?? {}), ...effective },
          },
        }));
      }
    },
    [],
  );

  const cancelEntry = useCallback((kind: EntryKind, id: string) => {
    setErrors((prev) => {
      const key = `${kind}:${id}`;
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    if (isDraftId(id)) {
      setDrafts((prev) => ({
        ...prev,
        [kind]: prev[kind].filter((d) => d.id !== id),
      }));
    } else {
      setEdits((prev) => {
        if (!(id in prev[kind])) return prev;
        const nextKind = { ...prev[kind] };
        delete nextKind[id];
        return { ...prev, [kind]: nextKind };
      });
      // Cancelling a delete-staged row un-stages it - same affordance,
      // symmetric with reverting an edit.
      setDeletes((prev) => {
        if (!prev[kind].has(id)) return prev;
        const nextKind = new Set(prev[kind]);
        nextKind.delete(id);
        return { ...prev, [kind]: nextKind };
      });
    }
  }, []);

  const markForDelete = useCallback((kind: EntryKind, id: string) => {
    if (!id || isDraftId(id)) return;
    setErrors((prev) => {
      const key = `${kind}:${id}`;
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setDeletes((prev) => {
      if (prev[kind].has(id)) return prev;
      const nextKind = new Set(prev[kind]);
      nextKind.add(id);
      return { ...prev, [kind]: nextKind };
    });
  }, []);

  const addDraft = useCallback((kind: EntryKind, payload: EntryValues) => {
    const sharedPublic = Boolean(payload.sharedPublic);
    const { sharedPublic: _ignored, ...rest } = payload;
    void _ignored;
    const fields = kind === "language" ? coupleLanguagePatch(rest) : rest;
    const id = `${DRAFT_PREFIX}${crypto.randomUUID()}`;
    const sharedWith = Array.isArray(payload.sharedWith) ? payload.sharedWith as ContentRecipient[]
      : sharedPublic ? recipientRelations(defaultRecipients) : [];
    setDrafts((prev) => ({
      ...prev,
      [kind]: [...prev[kind], { id, values: { ...fields, sharedWith, publiclyReadable: Boolean(payload.publiclyReadable) } }],
    }));
    return id;
  }, [defaultRecipients]);

  const discard = useCallback(() => {
    setEdits(EMPTY_EDITS);
    setDrafts(EMPTY_DRAFTS);
    setDeletes(EMPTY_DELETES);
    setErrors({});
  }, []);

  // Merge server-side rows with overlay + drafts for rendering. Saved rows
  // are normalized to UI shape first (DateInstant → "YYYY-MM" string,
  // country object → id string, activities array → joined string) so the
  // overlay's already-string values can spread on top cleanly. Drafts go
  // at the end of the list so existing entries don't visually shift when
  // a new one is added - matches the workshop convention where
  // "+ Hinzufügen" lives beneath the list.
  const entries = useMemo<MergedEntries>(() => {
    const merge = (
      kind: EntryKind,
      saved: readonly EntryValues[] | null | undefined,
    ): EntryValues[] => {
      const rows = (saved ?? []).map((row) => normalizeSavedRow(kind, row));
      const savedIds = new Set(rows.map((row) => str(row.id)));
      // A landed row gives way to its server row in the render that brings it, under the same id: an
      // editor open on it stays.
      for (const row of landed[kind]) if (!savedIds.has(row.id)) rows.push({ ...row.values, id: row.id });
      // Ordered on the saved values, before the edit overlay: changing a start date in an open row must
      // not move that row under the cursor; it takes its new place once saved (bd-requirements#50).
      if (kind === "education") rows.sort((a, b) => byStartNewestFirst(str(a.startDate), str(b.startDate)));
      const overlaid = rows
        .map((row) => {
          const slice = edits[kind][str(row.id)];
          return slice ? { ...row, ...slice } : row;
        })
        // Delete-staged rows disappear from the UI immediately so the marked
        // state is unmistakable; the actual remove mutation still only fires on
        // save (a Verwerfen/discard restores them, since it clears `deletes`).
        .filter((row) => !deletes[kind].has(str(row.id)) && !removed[kind].has(str(row.id)));
      const draftRows = drafts[kind].map((d) => ({ id: d.id, ...d.values }));
      return [...overlaid, ...draftRows];
    };
    return {
      experience: merge("experience", savedRowsOf(myself, "experience")),
      education: merge("education", savedRowsOf(myself, "education")),
      expertise: merge("expertise", savedRowsOf(myself, "expertise")),
      certificate: merge("certificate", savedRowsOf(myself, "certificate")),
      language: merge("language", savedRowsOf(myself, "language")),
    };
  }, [myself, edits, drafts, deletes, landed, removed]);

  // A landed row has done its job once the server rows hold it; kept beyond that, a row removed
  // elsewhere later would come back. A removed id stays hidden for good: ids are never reused, and a
  // reload that left before the delete may still bring the row after the server rows dropped it.
  useEffect(() => {
    if (!myself) return;
    const agreed: RowKey[] = [];
    for (const kind of ENTRY_KINDS) {
      const ids = new Set((savedRowsOf(myself, kind) ?? []).map((row) => str(row.id)));
      for (const row of landed[kind]) if (ids.has(row.id)) agreed.push({ kind, id: row.id });
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect -- syncing to the server rows, an outside source
    if (agreed.length > 0) setLanded((prev) => withoutRows(prev, agreed));
  }, [myself, landed]);

  const dirty = useMemo(() => {
    if (Object.keys(errors).length > 0) return true;
    for (const kind of ENTRY_KINDS) {
      if (Object.keys(edits[kind]).length > 0) return true;
      if (drafts[kind].length > 0) return true;
      if (deletes[kind].size > 0) return true;
    }
    return false;
  }, [edits, drafts, deletes, errors]);

  const save = useCallback(async (): Promise<void> => {
    interface Task {
      kind: EntryKind;
      id: string;
      run: () => Promise<unknown>;
      kindAction: "create" | "update" | "delete";
      // The edit or draft overlay sent. A newer one means typing went on meanwhile; a create's stands in
      // for the new row until the server rows hold it.
      sent?: EntryValues;
    }
    const tasks: Task[] = [];

    // Delete removed rows before creating or updating the remaining entries.
    for (const kind of ENTRY_KINDS) {
      for (const id of deletes[kind]) {
        tasks.push({
          kind,
          id,
          kindAction: "delete",
          run: () => callRemove(kind, hooks, id),
        });
      }
    }

    // Drafts → create
    for (const kind of ENTRY_KINDS) {
      for (const d of drafts[kind]) {
        const merged = (myself ? entries[kind].find((e) => e.id === d.id) : null) ?? {
          id: d.id,
          ...d.values,
        };
        tasks.push({
          kind,
          id: d.id,
          kindAction: "create",
          run: () => callAdd(kind, hooks, merged),
          sent: d.values,
        });
      }
    }

    // Edits → update. Skip rows that are also pending delete (the delete
    // wins; saving an update for a row we're about to remove is wasted).
    for (const kind of ENTRY_KINDS) {
      for (const id of Object.keys(edits[kind])) {
        if (deletes[kind].has(id)) continue;
        const merged = entries[kind].find((e) => e.id === id);
        if (!merged) continue; // Row was deleted server-side mid-edit - skip.
        tasks.push({
          kind,
          id,
          kindAction: "update",
          run: () => callUpdate(kind, hooks, id, merged),
          sent: edits[kind][id],
        });
      }
    }

    if (tasks.length === 0) return;

    // Commit guard: block a foreign language (not native) with no CEFR level.
    // Flag the offending rows and abort the whole save so nothing commits
    // half-valid; the user picks a level and re-saves (SMY-0050).
    const languageLevelRequired = intl.formatMessage({
      id: "profile.page.error.languageLevelRequired",
      defaultMessage: "Please choose a level for the foreign language.",
    });
    const langErrors: EntryErrors = {};
    const flagLang = (id: string, v: { nativeSpeaker?: unknown; cefr?: unknown } | undefined) => {
      if (v && !v.nativeSpeaker && !v.cefr) langErrors[`language:${id}`] = languageLevelRequired;
    };
    for (const d of drafts.language) {
      flagLang(d.id, (entries.language.find((e) => e.id === d.id) as { nativeSpeaker?: unknown; cefr?: unknown } | undefined) ?? d.values);
    }
    for (const id of Object.keys(edits.language)) {
      if (deletes.language.has(id)) continue;
      flagLang(id, entries.language.find((e) => e.id === id) as { nativeSpeaker?: unknown; cefr?: unknown } | undefined);
    }
    if (Object.keys(langErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...langErrors }));
      throw new LocalizedError(languageLevelRequired);
    }

    // Commit guard: per-row field lengths (the ImportedProfileSanitizer caps, which reach neither the
    // editor nor the review today) plus the date rules the review already enforces (shape, not in the
    // future, start<=end, not before the date of birth). Same shape as the language guard above — flag
    // the offending rows and abort the whole save, so nothing commits half-valid. Without this the
    // editor saved values the review refuses, e.g. a certificate issued before the person was born.
    const entryErrors: EntryErrors = {};
    const birthDate = getBirthDate?.();
    for (const kind of ENTRY_KINDS) {
      const flagRow = (id: string, values: EntryValues | undefined) => {
        if (!values) return;
        // Length before dates: an over-long value is the more basic problem, and reporting one reason
        // per row keeps the message the SaveBar shows unambiguous.
        const tooLong = entryFieldError(kind, values);
        if (tooLong) {
          entryErrors[`${kind}:${id}`] = entryFieldMessage(intl, tooLong);
          return;
        }
        if (!DATE_FIELDS[kind]) return;
        const bad = entryDateError(kind, values, birthDate);
        if (bad) entryErrors[`${kind}:${id}`] = entryDateMessage(intl, bad.error);
      };
      for (const d of drafts[kind]) {
        flagRow(d.id, entries[kind].find((e) => e.id === d.id) as EntryValues | undefined);
      }
      for (const id of Object.keys(edits[kind])) {
        if (deletes[kind].has(id)) continue;
        flagRow(id, entries[kind].find((e) => e.id === id) as EntryValues | undefined);
      }
    }
    if (Object.keys(entryErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...entryErrors }));
      throw new LocalizedError(Object.values(entryErrors)[0]!);
    }

    // Each outcome applies as its mutation lands, not after the last one: a reload during the save may
    // already bring a new row while the next is still being created, and its draft, still waiting for
    // the others, stood there next to it.
    const nextErrors: EntryErrors = {};
    let failureCount = 0;
    for (const task of tasks) {
      let value: unknown;
      try {
        value = await task.run();
      } catch (reason) {
        // Cancelled while it ran, the row is no longer anyone's to save: its failure is moot.
        if (!stillPending(overlaysNow.current, task)) continue;
        failureCount++;
        nextErrors[`${task.kind}:${task.id}`] = userFacingGraphqlError(intl, reason);
        continue;
      }
      const { kind, id, sent } = task;
      if (task.kindAction === "create") {
        setDrafts((prev) => ({ ...prev, [kind]: prev[kind].filter((d) => d.id !== id) }));
        if (typeof value === "string" && sent) {
          setLanded((prev) => withRows(prev, [{ kind, id: value, values: sent }]));
          // Typed into the draft while it was created: not sent, so it becomes a change to the new row.
          const typed = overlaysNow.current.drafts[kind].find((d) => d.id === id)?.values;
          if (typed && typed !== sent) {
            setEdits((prev) => ({ ...prev, [kind]: { ...prev[kind], [value]: { ...prev[kind][value], ...typed } } }));
          }
        }
      } else if (task.kindAction === "delete") {
        // A removed row's edit goes too — nothing is left to update, and kept it held the page unsaved —
        // and so does a landed row of that id.
        setDeletes((prev) => withoutIds(prev, [{ kind, id }]));
        setRemoved((prev) => withIds(prev, [{ kind, id }]));
        setLanded((prev) => withoutRows(prev, [{ kind, id }]));
        setEdits((prev) => withoutEdit(prev, kind, id));
      } else {
        // An edit typed on while its update ran was not sent, and stays. A landed row shows what was
        // sent from now on — not what its create sent — until the server rows bring it.
        setEdits((prev) => (prev[kind][id] === sent ? withoutEdit(prev, kind, id) : prev));
        if (sent) setLanded((prev) => withSentValues(prev, kind, id, sent));
      }
    }

    // Clear only the rows this save attempted, then apply its failures. Replacing the whole map would
    // work today — patchEntry and cancelEntry both drop a row's error, so no stale key survives — but it
    // makes that an invariant nothing enforces, and `dirty` counts a non-empty error map, so a wrongly
    // cleared map would silently un-dirty the SaveBar. Every other write to this map merges; this one now
    // does too.
    setErrors((prev) => {
      const next = { ...prev };
      for (const task of tasks) delete next[`${task.kind}:${task.id}`];
      return { ...next, ...nextErrors };
    });

    if (failureCount > 0) {
      const summary = intl.formatMessage(
        {
          id: "profile.page.error.saveEntriesFailedCount",
          defaultMessage:
            "{count, plural, one {# entry could not be saved.} other {# entries could not be saved.}}",
        },
        { count: failureCount },
      );
      // Second line: the specific reason(s), so the SaveBar shows more than a count.
      const reasons = [...new Set(Object.values(nextErrors))].join(" ");
      throw new LocalizedError(reasons ? `${summary}\n${reasons}` : summary);
    }
  }, [drafts, edits, deletes, entries, hooks, myself, intl, getBirthDate]);

  return {
    entries,
    draftIds,
    pendingDeletes: deletes,
    errors,
    patchEntry,
    cancelEntry,
    addDraft,
    markForDelete,
    dirty,
    save,
    discard,
  };
}

// === What a save wrote, until the server rows agree =========================

function savedRowsOf(
  myself: UseProfileEntriesStateArgs["myself"],
  kind: EntryKind,
): readonly EntryValues[] | null | undefined {
  switch (kind) {
    case "experience":
      return myself?.experiences;
    case "education":
      return myself?.educations;
    case "expertise":
      return myself?.expertises;
    case "certificate":
      return myself?.certifications;
    case "language":
      return myself?.languageSkills;
  }
}

interface RowKey {
  kind: EntryKind;
  id: string;
}

interface SavedRow extends RowKey {
  values: EntryValues;
}

function stillPending(
  overlays: { drafts: EntryDrafts; edits: EntryEdits; deletes: EntryDeletes },
  task: { kind: EntryKind; id: string; kindAction: "create" | "update" | "delete" },
): boolean {
  if (task.kindAction === "create") return overlays.drafts[task.kind].some((d) => d.id === task.id);
  if (task.kindAction === "delete") return overlays.deletes[task.kind].has(task.id);
  return task.id in overlays.edits[task.kind];
}

function withSentValues(prev: EntryDrafts, kind: EntryKind, id: string, sent: EntryValues): EntryDrafts {
  if (!prev[kind].some((row) => row.id === id)) return prev;
  return { ...prev, [kind]: prev[kind].map((row) => (row.id === id ? { ...row, values: { ...row.values, ...sent } } : row)) };
}

function withoutEdit(prev: EntryEdits, kind: EntryKind, id: string): EntryEdits {
  if (!(id in prev[kind])) return prev;
  const kindMap = { ...prev[kind] };
  delete kindMap[id];
  return { ...prev, [kind]: kindMap };
}

function withRows(prev: EntryDrafts, add: readonly SavedRow[]): EntryDrafts {
  if (add.length === 0) return prev;
  const next = { ...prev };
  for (const { kind, id, values } of add) next[kind] = [...next[kind], { id, values }];
  return next;
}

function withoutRows(prev: EntryDrafts, drop: readonly RowKey[]): EntryDrafts {
  const hit = drop.filter(({ kind, id }) => prev[kind].some((row) => row.id === id));
  if (hit.length === 0) return prev;
  const next = { ...prev };
  for (const { kind, id } of hit) next[kind] = next[kind].filter((row) => row.id !== id);
  return next;
}

function withIds(prev: EntryDeletes, add: readonly RowKey[]): EntryDeletes {
  if (add.length === 0) return prev;
  const next = { ...prev };
  for (const { kind, id } of add) next[kind] = new Set(next[kind]).add(id);
  return next;
}

function withoutIds(prev: EntryDeletes, drop: readonly RowKey[]): EntryDeletes {
  if (drop.length === 0) return prev;
  const next = { ...prev };
  for (const { kind, id } of drop) {
    const ids = new Set(next[kind]);
    ids.delete(id);
    next[kind] = ids;
  }
  return next;
}

// === Mutation dispatch ======================================================

interface EntryHookBundle {
  experience: UseExperiencesResult;
  education: UseEducationResult;
  expertise: UseExpertiseSkillsResult;
  certificate: UseCertificatesResult;
  language: UseLanguagesResult;
}

// Each branch pairs a typed builder with its matching hook, so the input
// type is checked against the hook signature - no `as` cast at the boundary.
async function callAdd(kind: EntryKind, hooks: EntryHookBundle, e: EntryValues): Promise<string> {
  switch (kind) {
    case "experience":
      return hooks.experience.add(buildExperienceInput(e));
    case "education":
      return hooks.education.add(buildEducationInput(e));
    case "expertise":
      return hooks.expertise.add(buildSkillInput(e));
    case "certificate":
      return hooks.certificate.add(buildCertificateInput(e));
    case "language":
      return hooks.language.add(buildLanguageInput(e));
  }
}

async function callUpdate(
  kind: EntryKind,
  hooks: EntryHookBundle,
  id: string,
  e: EntryValues,
): Promise<unknown> {
  switch (kind) {
    case "experience":
      return hooks.experience.update(id, buildExperienceInput(e));
    case "education":
      return hooks.education.update(id, buildEducationInput(e));
    case "expertise":
      return hooks.expertise.update(id, buildSkillInput(e));
    case "certificate":
      return hooks.certificate.update(id, buildCertificateInput(e));
    case "language":
      return hooks.language.update(id, buildLanguageInput(e));
  }
}

async function callRemove(
  kind: EntryKind,
  hooks: EntryHookBundle,
  id: string,
): Promise<unknown> {
  switch (kind) {
    case "experience":
      return hooks.experience.remove(id);
    case "education":
      return hooks.education.remove(id);
    case "expertise":
      return hooks.expertise.remove(id);
    case "certificate":
      return hooks.certificate.remove(id);
    case "language":
      return hooks.language.remove(id);
  }
}

// === Saved-row normalization to UI shape ===================================
// Once a saved row enters the merge step, we convert genesis shapes into
// the same shape MonthYearSelect / Select / EntryFields read on every
// keystroke. From there, the overlay can spread cleanly because both
// halves are strings/primitives. ProfilePage never sees a DateInstant.

function dateInstantToString(d: unknown, slice: 7 | 10): string {
  if (d == null) return "";
  if (typeof d === "object" && "date" in (d as object)) {
    const inner = (d as { date?: string | null }).date;
    return inner ? String(inner).slice(0, slice) : "";
  }
  return String(d).slice(0, slice);
}

function normalizeSavedRow(kind: EntryKind, row: EntryValues): EntryValues {
  if ((kind === "experience" || kind === "expertise") && row.classification != null && typeof row.classification !== "string")
    row = { ...row, classification: JSON.stringify(row.classification) };
  switch (kind) {
    case "experience":
      return {
        ...row,
        country: refId(row.country),
        industry: refId(row.industry),
        startDate: dateInstantToString(row.startDate, 7),
        endDate: dateInstantToString(row.endDate, 7),
        activities: Array.isArray(row.activities) ? row.activities.join("\n") : (row.activities ?? ""),
      };
    case "education":
      return {
        ...row,
        country: refId(row.country),
        degree: refId(row.degree),
        startDate: dateInstantToString(row.startDate, 7),
        endDate: dateInstantToString(row.endDate, 7),
      };
    case "expertise":
      return {
        ...row,
        level: row.level != null ? String(row.level) : "",
      };
    case "certificate":
      return {
        ...row,
        date: dateInstantToString(row.date, 10),
      };
    case "language":
      return {
        ...row,
        nativeSpeaker: Boolean(row.nativeSpeaker),
      };
  }
}

// === Entry → FormInput conversion ==========================================
// All rows reach these builders in UI shape (see normalizeSavedRow above),
// so `str` is a thin null-safe stringifier - no DateInstant handling. Each
// builder returns its hook's exact FormInput type, which is what makes the
// cast-free dispatch in callAdd / callUpdate type-check.

function buildExperienceInput(e: EntryValues): ExperienceFormInput {
  return {
    jobTitle: str(e.jobTitle),
    ...(e.classification !== undefined ? { classification: readClassification(e.classification) } : {}),
    employer: str(e.employer),
    department: str(e.department),
    location: str(e.location),
    country: str(e.country),
    industry: str(e.industry),
    startDate: str(e.startDate),
    endDate: str(e.endDate),
    activities: linesToList(str(e.activities)),
    sharedWith: sharedWithFromEntry(e),
    publiclyReadable: Boolean(e.publiclyReadable),
    allGroups: Boolean(e.allGroups),
  };
}

function buildEducationInput(e: EntryValues): EducationFormInput {
  return {
    field: str(e.field),
    institution: str(e.institution),
    degree: str(e.degree),
    city: str(e.city),
    country: str(e.country),
    startDate: str(e.startDate),
    endDate: str(e.endDate),
    sharedWith: sharedWithFromEntry(e),
    publiclyReadable: Boolean(e.publiclyReadable),
    allGroups: Boolean(e.allGroups),
  };
}

function buildSkillInput(e: EntryValues): SkillFormInput {
  return {
    description: str(e.description),
    ...(e.classification !== undefined ? { classification: readClassification(e.classification) } : {}),
    position: typeof e.position === "number" ? e.position : Number(e.position) || 0,
    level: typeof e.level === "number" ? e.level : Number(e.level) || 0,
    sharedWith: sharedWithFromEntry(e),
    publiclyReadable: Boolean(e.publiclyReadable),
    allGroups: Boolean(e.allGroups),
  };
}

function buildCertificateInput(e: EntryValues): CertificateFormInput {
  return {
    name: str(e.name),
    date: str(e.date),
    sharedWith: sharedWithFromEntry(e),
    publiclyReadable: Boolean(e.publiclyReadable),
    allGroups: Boolean(e.allGroups),
  };
}

function buildLanguageInput(e: EntryValues): LanguageFormInput {
  return {
    languageTag: str(e.languageTag),
    cefr: str(e.cefr),
    nativeSpeaker: Boolean(e.nativeSpeaker),
    selfAssessed: Boolean(e.selfAssessed),
    sharedWith: sharedWithFromEntry(e),
    publiclyReadable: Boolean(e.publiclyReadable),
    allGroups: Boolean(e.allGroups),
  };
}

function str(v: unknown): string {
  return v == null ? "" : String(v);
}

// genesis stores some references as { id } objects and others as bare id
// strings; normalize either to a plain id string for the UI layer.
function refId(v: unknown): string {
  if (v && typeof v === "object" && "id" in v) {
    return str((v as { id?: unknown }).id);
  }
  return str(v);
}

function linesToList(value: string): string[] {
  return value
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

function sharedWithFromEntry(e: EntryValues): ContentRecipient[] {
  const raw = e.sharedWith;
  if (Array.isArray(raw)) return raw as ContentRecipient[];
  return [];
}
